import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
    Briefcase,
    Download,
    FileText,
    GripVertical,
    Info,
    Search,
    X,
} from "lucide-react";
import { getAllJobsRequest } from "../../redux/actions/Jobactions";
import "./JobDescriptionGenerator.css";

const SECTIONS = [
    {
        id: "title",
        title: "Intitulé du Poste",
        items: ["Nom du poste", "Position dans l'organisation"],
    },
    {
        id: "description",
        title: "Description du Poste",
        items: ["Missions principales", "Activités et responsabilités", "Objectifs"],
    },
    {
        id: "profile",
        title: "Profil Recherché",
        items: ["Compétences techniques", "Soft skills", "Formation / diplômes", "Années d'expérience"],
    },
    {
        id: "conditions",
        title: "Conditions d'Exercice",
        items: ["Lieu de travail", "Spécificités"],
    },
];

const buildInitialSelection = () => SECTIONS.reduce((acc, section) => {
    acc[section.id] = {
        enabled: true,
        items: section.items.reduce((items, item) => ({ ...items, [item]: true }), {}),
    };
    return acc;
}, {});

const JobDescriptionGenerator = ({ jobs, loading, dispatch }) => {
    const [selectedJobIds, setSelectedJobIds] = useState([]);
    const [jobDropdownOpen, setJobDropdownOpen] = useState(false);
    const [jobSearch, setJobSearch] = useState("");
    const [conditions, setConditions] = useState("");
    const [selection, setSelection] = useState(buildInitialSelection);

    useEffect(() => {
        dispatch(getAllJobsRequest());
    }, [dispatch]);

    const activeJobs = useMemo(() => (
        Array.isArray(jobs) ? jobs.filter((job) => job?.isActive !== false) : []
    ), [jobs]);

    const selectedJobs = useMemo(() => {
        const ids = new Set(selectedJobIds.map(String));
        return activeJobs.filter((job) => ids.has(String(job.id)));
    }, [activeJobs, selectedJobIds]);

    const filteredJobs = useMemo(() => {
        const query = jobSearch.trim().toLowerCase();
        if (!query) return activeJobs;
        return activeJobs.filter((job) =>
            job.name?.toLowerCase().includes(query) ||
            job.code?.toLowerCase().includes(query)
        );
    }, [activeJobs, jobSearch]);

    const allItemsCount = useMemo(() => SECTIONS.reduce((sum, section) => sum + section.items.length, 0), []);
    const selectedItemsCount = useMemo(() => SECTIONS.reduce((sum, section) => {
        if (!selection[section.id]?.enabled) return sum;
        return sum + section.items.filter((item) => selection[section.id]?.items?.[item]).length;
    }, 0), [selection]);

    const toggleJob = (jobId) => {
        const id = String(jobId);
        setSelectedJobIds((prev) =>
            prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
        );
    };

    const removeJob = (jobId) => {
        const id = String(jobId);
        setSelectedJobIds((prev) => prev.filter((value) => value !== id));
    };

    const toggleSection = (sectionId) => {
        setSelection((prev) => ({
            ...prev,
            [sectionId]: {
                ...prev[sectionId],
                enabled: !prev[sectionId].enabled,
            },
        }));
    };

    const toggleItem = (sectionId, item) => {
        setSelection((prev) => ({
            ...prev,
            [sectionId]: {
                ...prev[sectionId],
                items: {
                    ...prev[sectionId].items,
                    [item]: !prev[sectionId].items[item],
                },
            },
        }));
    };

    const generateStaticWord = () => {
        const jobsForDoc = selectedJobs.length ? selectedJobs : [{ name: "Poste exemple", code: "JOB_DEMO" }];
        const htmlSections = SECTIONS
            .filter((section) => selection[section.id]?.enabled)
            .map((section) => {
                const items = section.items.filter((item) => selection[section.id]?.items?.[item]);
                if (!items.length) return "";
                return `
                    <h2>${section.title}</h2>
                    <ul>${items.map((item) => `<li><strong>${item} :</strong> Contenu exemple à compléter.</li>`).join("")}</ul>
                `;
            })
            .join("");

        const html = `
            <html>
                <head><meta charset="utf-8"><title>Fiche de poste</title></head>
                <body>
                    <h1>Fiche de poste</h1>
                    <p><strong>Postes :</strong> ${jobsForDoc.map((job) => `${job.name || job.code} (${job.code || "-"})`).join(", ")}</p>
                    <p><strong>Conditions :</strong> ${conditions || "Conditions exemple à compléter."}</p>
                    ${htmlSections}
                </body>
            </html>
        `;
        const blob = new Blob(["\ufeff", html], { type: "application/msword" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "fiche-de-poste.doc";
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="jdg-page">
            <header className="jdg-hero">
                <div className="jdg-hero-main">
                    <div className="jdg-hero-icon">
                        <FileText size={32} />
                    </div>
                    <div>
                        <h1>Générateur de Fiche de Poste</h1>
                        <p>Créez des fiches de poste professionnelles avec l'intelligence artificielle</p>
                    </div>
                </div>
                <button className="jdg-generate" type="button" onClick={generateStaticWord}>
                    <Download size={16} />
                    Générer la fiche
                    <span>{selectedItemsCount}/{allItemsCount}</span>
                </button>
            </header>

            <main className="jdg-content">
                <section className="jdg-top-grid">
                    <div className="jdg-panel">
                        <div className="jdg-panel-title">
                            <FileText size={18} />
                            <h2>Postes</h2>
                        </div>
                        <label className="jdg-label">Sélectionner un ou plusieurs postes *</label>
                        <div className="jdg-job-select">
                            <button
                                className="jdg-job-trigger"
                                type="button"
                                onClick={() => setJobDropdownOpen((open) => !open)}
                            >
                                <span>
                                    {selectedJobs.length
                                        ? `${selectedJobs.length} poste(s) sélectionné(s)`
                                        : "Choisir les postes de l'entreprise..."}
                                </span>
                            </button>

                            {selectedJobs.length > 0 && (
                                <div className="jdg-selected-jobs">
                                    {selectedJobs.map((job) => (
                                        <span className="jdg-job-chip" key={job.id}>
                                            {job.name || job.code}
                                            <button type="button" onClick={() => removeJob(job.id)} aria-label="Retirer">
                                                <X size={12} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            {jobDropdownOpen && (
                                <div className="jdg-job-menu">
                                    <div className="jdg-search-wrap">
                                        <Search size={15} />
                                        <input
                                            value={jobSearch}
                                            onChange={(e) => setJobSearch(e.target.value)}
                                            placeholder="Rechercher un poste..."
                                        />
                                    </div>
                                    <div className="jdg-job-list">
                                        {loading ? (
                                            <div className="jdg-empty">Chargement des postes...</div>
                                        ) : filteredJobs.length === 0 ? (
                                            <div className="jdg-empty">Aucun poste trouvé</div>
                                        ) : filteredJobs.map((job) => {
                                            const checked = selectedJobIds.includes(String(job.id));
                                            return (
                                                <label className="jdg-job-option" key={job.id}>
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleJob(job.id)}
                                                    />
                                                    <span>
                                                        <strong>{job.name || job.code}</strong>
                                                        <small>{job.code || "Sans code"}</small>
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="jdg-panel">
                        <div className="jdg-panel-title">
                            <Briefcase size={18} />
                            <h2>Conditions du Poste</h2>
                        </div>
                        <label className="jdg-label">Conditions du Poste *</label>
                        <textarea
                            value={conditions}
                            onChange={(e) => setConditions(e.target.value)}
                            placeholder="Ex: CDI, 40h/semaine, travail remote à Casablanca, salaire compétitif..."
                        />
                    </div>
                </section>

                <div className="jdg-info">
                    <Info size={15} />
                    <span>Glissez-déposez pour réorganiser les sections. Cochez/décochez pour inclure ou exclure des éléments.</span>
                </div>

                <section className="jdg-sections">
                    {SECTIONS.map((section, index) => (
                        <article className="jdg-section-card" key={section.id}>
                            <div className="jdg-section-head">
                                <div className="jdg-section-left">
                                    <GripVertical size={16} />
                                    <input
                                        type="checkbox"
                                        checked={selection[section.id]?.enabled}
                                        onChange={() => toggleSection(section.id)}
                                    />
                                    <h3>{index + 1}. {section.title}</h3>
                                </div>
                                <span className="jdg-order">{index + 1}</span>
                            </div>
                            <div className="jdg-section-items">
                                {section.items.map((item) => (
                                    <label key={item} className="jdg-check-row">
                                        <input
                                            type="checkbox"
                                            checked={!!selection[section.id]?.items?.[item]}
                                            disabled={!selection[section.id]?.enabled}
                                            onChange={() => toggleItem(section.id, item)}
                                        />
                                        <span>{item}</span>
                                    </label>
                                ))}
                            </div>
                        </article>
                    ))}
                </section>

            </main>
        </div>
    );
};

const mapStateToProps = (state) => ({
    jobs: state.jobs?.jobs || [],
    loading: state.jobs?.loading || false,
});

const mapDispatchToProps = (dispatch) => ({ dispatch });

export default connect(mapStateToProps, mapDispatchToProps)(JobDescriptionGenerator);
